import { DateTime } from "luxon"
import { DATE_SUBTYPES, resolveFieldSubtype } from "../../../helpers/dateNormalization"

/* Check whether a value looks like an ISO date string (simple heuristic) */
function isIsoDateString(v) {
  if (typeof v !== "string") return false
  // Use Luxon for robust validation (e.g. handles leap years correctly)
  // require at least 10 chars (YYYY-MM-DD) to avoid matching just "YYYY" or "YYYY-MM"
  if (v.length < 10) return false
  return DateTime.fromISO(v).isValid
}

/* Return true for non-null plain objects (not arrays) */
function isObjectLike(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

/* Safely parse a field_conditional value (JSON string or object) */
function parseConditional(cond) {
  if (!cond) return null
  if (typeof cond === "object") return cond
  try {
    return JSON.parse(String(cond))
  } catch {
    return null
  }
}

/* Normalize a primitive or common object shapes into a lowercase string for comparisons */
function normalizePrimitiveForComparison(v) {
  if (v == null) return null
  if (typeof v === "boolean") return v ? "yes" : "no"
  if (typeof v === "object") {
    if (v.label) return String(v.label).toLowerCase()
    if (v.value) return String(v.value).toLowerCase()
    if (v.key) return String(v.key).toLowerCase()
    try {
      return JSON.stringify(v).toLowerCase()
    } catch {
      return String(v).toLowerCase()
    }
  }
  return String(v).toLowerCase()
}

/* Normalize parent field values into an array of comparable strings for conditional checks */
function normalizeParentValue(val) {
  if (Array.isArray(val))
    return val.map(normalizePrimitiveForComparison).filter(Boolean)
  const p = normalizePrimitiveForComparison(val)
  return p ? [p] : []
}

/* Resolve a stored option value to its human label from a field_options array */
function resolveOptionLabel(singleValue, options = []) {
  if (singleValue == null) return null
  const target =
    typeof singleValue === "object"
      ? String(
          singleValue.value ??
            singleValue.key ??
            singleValue.label ??
            JSON.stringify(singleValue),
        )
      : String(singleValue)
  for (const opt of options) {
    if (!opt) continue
    if (opt.value !== undefined && String(opt.value) === target)
      return opt.label ?? opt.value
    if (opt.key !== undefined && String(opt.key) === target)
      return opt.label ?? opt.key
    if (opt.label !== undefined && String(opt.label) === target)
      return opt.label
  }
  return null
}

// Internal helper for formatting simple "leaf" values (Date, ISO string, simple object)
// to reduce duplication in formatPrimitive
function formatLeafValue(value, options = {}) {
  if (value instanceof Date) return fmtDate(value, options)
  if (typeof value === "string" && isIsoDateString(value))
    return fmtDate(value, options)
  if (isObjectLike(value))
    return (
      value.label ?? value.value ?? value.key ?? JSON.stringify(value, null, 2)
    )
  return String(value)
}

/* Format a variety of primitive values (strings, arrays, objects, booleans) for display */
function formatPrimitive(value, fieldType, options = {}) {
  const { forceDateOnly = false, forceTimeOnly = false } = options

  if (value == null || value === "") return "—"

  if (value instanceof Date)
    return fmtDate(value, { forceDateOnly, forceTimeOnly })

  const ftLower = String(fieldType || "").toLowerCase()
  if (ftLower === "file_image" || ftLower === "file") {
    // Extract only the file name from a path
    const strValue = String(value)
    // split by forward slash or backslash
    const parts = strValue.split(/[/\\]/)
    return parts[parts.length - 1] || "—"
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return "—"
    return value.map((it) => formatLeafValue(it, options)).join("\n")
  }

  if (isObjectLike(value)) {
    if (value.label || value.value || value.key)
      return value.label ?? value.value ?? value.key
    const entries = Object.entries(value)
    if (entries.length === 0) return "—"

    const formattedEntries = entries.map(([k, v]) => {
      let valStr
      if (Array.isArray(v)) {
        valStr = v.map((it) => formatLeafValue(it, options)).join(", ") || "—"
      } else if (
        v instanceof Date ||
        (typeof v === "string" && isIsoDateString(v))
      ) {
        valStr = fmtDate(v, options)
      } else {
        valStr = String(v ?? "—")
      }
      return `${k}: ${valStr}`
    })

    // Heuristic: If any formatted string contains a 3-letter word (like "Jan", "key") or a 4-digit number (year),
    // we assume it's data that should be displayed as a list of lines.
    // Otherwise it might be a raw config object better shown as JSON.
    if (
      formattedEntries.some((s) => /\b[A-Za-z]{3}\b/.test(s) || /\d{4}/.test(s))
    ) {
      return formattedEntries.join("\n")
    }
    return JSON.stringify(value, null, 2)
  }

  if (typeof value === "string" && isIsoDateString(value))
    return fmtDate(value, { forceDateOnly, forceTimeOnly })
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (fieldType && String(fieldType).toLowerCase().includes("date"))
    return fmtDate(value, { forceDateOnly, forceTimeOnly })
  return String(value)
}

/* Convert months integer into "X year(s), Y month(s)" */
function formatMonthsAsYearsMonths(monthsRaw) {
  if (monthsRaw == null || monthsRaw === "") return "—"
  const n = Number(monthsRaw)
  if (!Number.isFinite(n) || n <= 0) return "—"
  const total = Math.floor(Math.abs(n))
  const years = Math.floor(total / 12)
  const months = total % 12
  return [
    years > 0 ? `${years} year${years === 1 ? "" : "s"}` : null,
    months > 0 ? `${months} month${months === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(", ")
}

/* Format a Date or date-like value into a readable string (date or date+time) */
function fmtDate(value, { forceDateOnly = false, forceTimeOnly = false } = {}) {
  if (value == null || value === "") return "—"
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d)) return String(value)

  if (forceTimeOnly) {
    return d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    })
  }

  const hasTime =
    value instanceof Date
      ? d.getHours() || d.getMinutes() || d.getSeconds()
      : String(value).includes("T") || String(value).includes(":")
  if (hasTime && !forceDateOnly) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    })
  }
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })
}

// helper: determine if a microservice value should be considered "checked"
function isMicroserviceChecked(v) {
  if (Array.isArray(v))
    return (
      v.length > 0 && v.some((it) => it != null && String(it).trim() !== "")
    )
  if (v === true) return true
  if (typeof v === "string") return v.trim() !== ""
  if (typeof v === "number") return true
  return false
}

// Internal helper for formatting enterprise_features
function _formatEnterpriseFeatures(blockValues) {
  if (!blockValues || typeof blockValues !== "object") return "—"
  const selected = []
  for (const [k, v] of Object.entries(blockValues)) {
    if (!k.startsWith("features_")) continue
    if (isMicroserviceChecked(v)) {
      const namePart = k.replace(/^features_/, "")
      selected.push(humanizeFieldName(namePart))
    }
  }
  if (selected.length === 0) return "—"
  return selected.join("\n")
}

// Internal helper for formatting organization_microservices
function _formatOrganizationMicroservices(blockValues) {
  if (!blockValues || typeof blockValues !== "object") return "—"
  const selected = []
  for (const [k, v] of Object.entries(blockValues)) {
    if (!k.startsWith("microservice_")) continue
    if (isMicroserviceChecked(v)) {
      const namePart = k.replace(/^microservice_/, "")
      selected.push(humanizeFieldName(namePart))
    }
  }
  if (selected.length === 0) return "—"
  return selected.join("\n")
}

/* Return true for field types that should be hidden in the summary (e.g. hidden) */
function isSkippableFieldType(t) {
  if (!t) return false
  return String(t).toLowerCase() === "hidden"
}

/* A set of field names to always exclude from the summary view */
/* These are rendered as type=hidden fields in the form */
const EXCLUDED_FIELD_NAMES = new Set([
  "assessment_score",
  "assessment_type",
  "assessment_8_dimensions",
  "organization_enterprise_id",
  "enterprise_managed",
  "id_creator",
  // System date metadata — hidden from summary view
  "date_changed",
  "date_created",
  "date_modified",
  "date_notified",
])

/* Check if a field name is in the exclusion list */
function isExcludedFieldName(name) {
  if (!name) return false
  return EXCLUDED_FIELD_NAMES.has(String(name))
}

/* Detect password-like field names to avoid rendering sensitive values */
function isPasswordFieldName(name) {
  if (!name) return false
  return String(name).toLowerCase().includes("password")
}

/* Check whether a field's conditional rules are satisfied given block/row values */
function conditionalMet(field, blockValues = {}, row = null) {
  const raw = parseConditional(field?.field_conditional)
  if (!raw || typeof raw !== "object") return true
  for (const [parentName, allowed] of Object.entries(raw)) {
    const allowedArr = Array.isArray(allowed) ? allowed : [allowed]
    const allowedNormalized = allowedArr.map((a) =>
      a == null ? "" : String(a).toLowerCase(),
    )
    const parentRaw =
      row && row[parentName] !== undefined
        ? row[parentName]
        : blockValues[parentName]
    if (parentRaw == null) return false
    const parentVals = normalizeParentValue(parentRaw)
    if (!parentVals.some((pv) => allowedNormalized.includes(pv))) return false
  }
  return true
}

/* Turn a snake_case field_name into a human-friendly label */
function humanizeFieldName(name = "") {
  return String(name)
    .replace(/^_+|_+$/g, "") // trim leading/trailing underscores
    .replace(/_+/g, " ") // replace one or more underscores with spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()) // capitalize first letter of each word
}

/* Smoothly scroll the main content area to the top */
export function smoothScrollToTop(targetElement = null) {
  const scrollTarget = targetElement || document.querySelector(".body-main")

  if (scrollTarget) {
    const currentScrollTop = scrollTarget.scrollTop
    const duration = 400 // ms
    const startTime = performance.now()

    const scrollStep = (timestamp) => {
      const elapsed = timestamp - startTime
      const progress = Math.min(elapsed / duration, 1)
      scrollTarget.scrollTop = currentScrollTop * (1 - progress)
      if (progress < 1) {
        requestAnimationFrame(scrollStep)
      }
    }
    requestAnimationFrame(scrollStep)
  }
}

/* Strip HTML tags and decode entities (used for textarea_rich display) */
function stripHtml(html) {
  if (html == null) return ""
  try {
    const container =
      typeof document !== "undefined" ? document.createElement("div") : null
    if (container) {
      container.innerHTML = String(html)
      return container.textContent || container.innerText || ""
    }
    // Naive fallback if DOM is not available
    return String(html).replace(/<\/?[^>]+(>|$)/g, "")
  } catch {
    return String(html).replace(/<\/?[^>]+(>|$)/g, "")
  }
}

/* Resolve/format a stored field value to something displayable (handles options + special cases) */
function formatValueForField(value, field = {}, blockValues = {}) {
  // Determine display mode based on field subtype
  const resolvedSubtype = resolveFieldSubtype(field)

  let forceDateOnly = true
  let forceTimeOnly = false

  if (resolvedSubtype === DATE_SUBTYPES.TIME) {
    forceTimeOnly = true
    forceDateOnly = false
  } else if (resolvedSubtype === DATE_SUBTYPES.DATETIME) {
    forceDateOnly = false
  }

  // special-case: force date-only formatting for birthdate fields
  if (
    field &&
    (field.field_name === "account_birthdate" ||
      field.field_name === "demo_birthdate")
  ) {
    return fmtDate(value, { forceDateOnly: true })
  }

  // special-case: aggregate microservice_* keys when the canonical field is organization_microservices or enterprise_microservices
  if (
    field &&
    (String(field.field_name) === "organization_microservices" ||
      String(field.field_name) === "enterprise_microservices")
  ) {
    return _formatOrganizationMicroservices(blockValues)
  }

  // special-case: aggregate features_* keys when the canonical field is enterprise_features
  if (field && String(field.field_name) === "enterprise_features") {
    return _formatEnterpriseFeatures(blockValues)
  }

  if (
    field &&
    String(field.field_name) === "participant_address_lives_primary_length"
  )
    return formatMonthsAsYearsMonths(value)

  const opts = Array.isArray(field.field_options) ? field.field_options : []
  if (value != null && value !== "" && opts.length > 0) {
    if (Array.isArray(value)) {
      const mapped = value
        .map(
          (v) =>
            resolveOptionLabel(v, opts) ??
            (isObjectLike(v)
              ? (v.label ?? v.value ?? JSON.stringify(v, null, 2))
              : String(v)),
        )
        .filter((s) => s != null && s !== "")
      if (mapped.length === 0) return "—"
      return mapped.join("\n")
    }
    const label = resolveOptionLabel(value, opts)
    if (label != null) return label
    if (isObjectLike(value)) {
      const inner = value.value ?? value.key ?? value.label
      if (inner !== undefined) {
        const innerLabel = resolveOptionLabel(inner, opts)
        if (innerLabel != null) return innerLabel
      }
    }
  }
  return formatPrimitive(value, field.field_type, {
    forceDateOnly,
    forceTimeOnly,
  })
}

/* A simple heuristic to check if a string is a stringified JSON object/array */
function isJsonString(str) {
  if (typeof str !== "string") return false
  try {
    const parsed = JSON.parse(str)
    return typeof parsed === "object" && parsed !== null
  } catch (e) {
    return false
  }
}

export {
  fmtDate,
  isSkippableFieldType,
  isExcludedFieldName,
  isPasswordFieldName,
  conditionalMet,
  humanizeFieldName,
  stripHtml,
  formatValueForField,
  isJsonString,
  isMicroserviceChecked,
}
